/**
 * toolTemplates — the FE's connector-tool vocabulary: display name + form spec
 * per `tool_name`.
 *
 * The form specs are imported from `content/Chat/Actions/ActionsTemplates` rather
 * than copied: they are the specs the connector actions are authored against, and
 * a second copy would drift. Temporary — they move into this module when that one
 * is retired.
 */

import { ActionsTemplates } from 'src/content/Chat/Actions/ActionsTemplates';

/** One editable field of a tool's approval form, as the template declares it. */
export interface ToolTemplateField {
  key: string;
  label: string;
  multiline?: boolean;
  isArray?: boolean;
}

/**
 * The connector display name for a tool, e.g. `gmail__send_email` → **"Sending Email
 * via Gmail"**.
 */
export function toolDisplayName(
  toolName: string | undefined
): string | undefined {
  if (!toolName) {
    return undefined;
  }
  return ActionsTemplates[toolName]?.displayName;
}

/**
 * The approval-form fields for a tool.
 */
export function toolTemplateFields(
  toolName: string | undefined,
  args: Record<string, unknown> | undefined
): ToolTemplateField[] {
  const template = toolName ? ActionsTemplates[toolName] : undefined;
  if (template) {
    return template.fields;
  }
  return Object.keys(args ?? {}).map((key) => ({ key, label: key }));
}

/**
 * One argument value → the string the text control shows.
 */
export function readFieldValue(
  args: Record<string, unknown> | undefined,
  key: string
): string {
  const raw = args?.[key];
  if (raw === null || raw === undefined) {
    return '';
  }
  if (typeof raw === 'object') {
    return JSON.stringify(raw, null, 2);
  }
  return String(raw);
}

/**
 * A field's edited text, cast back to the type its original arg value had.
 *
 * The approval form is all text inputs, so an edited (or merely re-submitted
 * untouched) number/boolean argument arrives here as a string — e.g. a
 * connector action's `limit: 100` becomes `"100"`. Left uncoerced, that
 * string then fails the connector's JSON-schema validation (`"100" is not
 * of type 'integer'"`) even though nothing meaningful changed. Only
 * `number`/`boolean` originals are coerced; a string stays a string
 * unchanged, exactly as before.
 */
function coerceToOriginalType(value: string, originalValue: unknown): unknown {
  if (typeof originalValue === 'number') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : parsed;
  }
  if (typeof originalValue === 'boolean') {
    const lowered = value.trim().toLowerCase();
    if (lowered === 'true') return true;
    if (lowered === 'false') return false;
    return value;
  }
  return value;
}

/**
 * The edited form values folded back onto the original `args`.
 */
export function applyFieldValues(
  args: Record<string, unknown> | undefined,
  values: Record<string, string>
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(args ?? {}) };
  for (const [key, value] of Object.entries(values)) {
    if (typeof value !== 'string') {
      continue;
    }
    const trimmed = value.trim();
    if (trimmed === '') {
      continue;
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        next[key] = JSON.parse(trimmed);
        continue;
      } catch {
      }
    }
    next[key] = coerceToOriginalType(value, args?.[key]);
  }
  return next;
}
